/** Shelf labels are not one format, so the camera reads the common 1D symbologies and QR alike. */
export const CAMERA_BARCODE_FORMATS = [
  'QR_CODE',
  'CODE_128',
  'CODE_39',
  'CODE_93',
  'CODABAR',
  'EAN_13',
  'EAN_8',
  'ITF',
  'UPC_A',
  'UPC_E',
] as const;

export const CAMERA_DENIED_MESSAGE =
  'Camera access was denied. Allow the camera for this site in the browser settings, then try again.';
export const NO_CAMERA_MESSAGE = 'No camera was found on this device.';
export const CAMERA_FAILED_MESSAGE = 'The camera could not be started. Close other apps using it, then try again.';

/** The operator-facing reason a camera did not open. */
export const describeCameraError = (error: unknown): string => {
  const name = error instanceof Error || error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return CAMERA_DENIED_MESSAGE;
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return NO_CAMERA_MESSAGE;
  }
  return CAMERA_FAILED_MESSAGE;
};

/** Opens the rear camera. A browser without camera access at all (no device API, or an insecure page) has no camera. */
export const openRearCamera = async (): Promise<MediaStream> => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new DOMException(NO_CAMERA_MESSAGE, 'NotFoundError');
  }
  return navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' } } });
};

/** Every track goes, not just the video one, so the browser's camera indicator turns off. */
export const releaseCamera = (stream: MediaStream | null | undefined) => {
  stream?.getTracks().forEach((track) => track.stop());
};

/**
 * Plays the stream in the video and reports the first code read, once. The decoder is loaded on first use,
 * so a desktop that never opens the camera never downloads it. The returned stop ends the decode loop.
 */
export const startBarcodeDecoding = async (
  stream: MediaStream,
  video: HTMLVideoElement,
  onCode: (text: string) => void,
): Promise<() => void> => {
  const { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType } = await import('@zxing/library');
  const hints = new Map<number, unknown>([
    [DecodeHintType.POSSIBLE_FORMATS, CAMERA_BARCODE_FORMATS.map((format) => BarcodeFormat[format])],
    [DecodeHintType.TRY_HARDER, true],
  ]);
  const reader = new BrowserMultiFormatReader(hints);
  let done = false;
  const stop = () => {
    done = true;
    reader.reset();
  };
  void reader
    .decodeFromStream(stream, video, (result) => {
      const text = result?.getText();
      if (!done && text) {
        stop();
        onCode(text);
      }
    })
    .catch(() => undefined);
  return stop;
};
